import React from "react";

type Step = {
  number: number;
  label: string;
  isActive?: boolean;
  isCompleted?: boolean;
  description?: string;
};

type StepperProps = {
  steps?: Step[];
  orientation?: "horizontal" | "vertical";
  showStepNumbers?: boolean;
  activeColor?: string;
  completedColor?: string;
  inactiveColor?: string;
  showDescriptions?: boolean;
  customCompletedIcon?: React.ReactNode;
};

const Stepper: React.FC<StepperProps> = ({
  steps,
  orientation = "horizontal",
  showStepNumbers = true,
  activeColor = "#4A5568",
  completedColor = "#48BB78",
  inactiveColor = "#A0AEC0",
  showDescriptions = false,
  customCompletedIcon
}) => {
  const isVertical = orientation === "vertical";

  return (
    <section id="Stepper" className="bg-[#F3F1E6]">
      <div className={`flex ${isVertical ? "flex-col" : "justify-center"}`}>
        <ol
          className={`
          ${isVertical ? "flex-col space-y-4" : "flex justify-center space-x-1 sm:space-x-4"}
          flex w-full  px-2 py-5 text-center text-sm font-medium sm:py-8
        `}
        >
          {steps?.map((step, index) => (
            <li
              key={index}
              className={`
                flex flex-1 flex-col items-center
                ${step.isActive ? `text-[${activeColor}]` : `text-[${inactiveColor}]`}
              `}
            >
              <span
                className={`
                  ${isVertical ? "mr-4" : "mb-2 md:-mb-0 md:mr-2"}
                  flex h-5 w-5 items-center justify-center border text-xs
                  ${
                    step.isCompleted
                      ? `bg-[${completedColor}] text-white`
                      : `bg-light-50 border-[${inactiveColor}] text-[${inactiveColor}]`
                  }
                  shrink-0 rounded-full
                `}
              >
                {step.isCompleted
                  ? customCompletedIcon || (
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                          d="M18.7 7.20039C18.3 6.80039 17.7 6.80039 17.3 7.20039L9.8 14.7004L6.7 11.6004C6.3 11.2004 5.7 11.2004 5.3 11.6004C4.9 12.0004 4.9 12.6004 5.3 13.0004L9.1 16.8004C9.3 17.0004 9.5 17.1004 9.8 17.1004C10.1 17.1004 10.3 17.0004 10.5 16.8004L18.7 8.60039C19.1 8.20039 19.1 7.60039 18.7 7.20039Z"
                          fill="currentColor"
                        />
                      </svg>
                    )
                  : showStepNumbers && step.number}
              </span>
              <div className={`flex flex-col items-${isVertical ? "start" : "center"}`}>
                <span className={`text-xs md:text-sm ${step.isActive ? "font-semibold" : "opacity-40"}`}>
                  {step.label}
                </span>
                {showDescriptions && step.description && (
                  <span className="mt-1 text-xs text-gray-500">{step.description}</span>
                )}
              </div>
            </li>
          ))}
        </ol>
      </div>
    </section>
  );
};

export default Stepper;
